import { useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { MapPin } from '@phosphor-icons/react';

export function PropertyMap({ address, propertyTitle }) {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);

    const hasCoordinates = address?.lat && address?.lng;
    const hasAddress = address?.city || address?.street;

    useEffect(() => {
        // Dynamically load Leaflet CSS and JS
        if (!document.getElementById('leaflet-css')) {
            const link = document.createElement('link');
            link.id = 'leaflet-css';
            link.rel = 'stylesheet';
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            document.head.appendChild(link);
        }

        const loadLeaflet = async () => {
            if (!window.L) {
                await new Promise((resolve) => {
                    const script = document.createElement('script');
                    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
                    script.onload = resolve;
                    document.head.appendChild(script);
                });
            }
            
            initMap();
        };

        const initMap = async () => {
            if (!mapRef.current || !window.L) return;

            // Clean up existing map
            if (mapInstance.current) {
                mapInstance.current.remove();
            }

            let lat, lng;

            if (hasCoordinates) {
                lat = parseFloat(address.lat);
                lng = parseFloat(address.lng);
            } else if (hasAddress) {
                // Geocode address using Nominatim (OpenStreetMap)
                try {
                    const searchQuery = [
                        address.street,
                        address.house_number,
                        address.zip,
                        address.city,
                        address.country || 'Germany'
                    ].filter(Boolean).join(', ');

                    const response = await fetch(
                        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`
                    );
                    const data = await response.json();

                    if (data && data.length > 0) {
                        lat = parseFloat(data[0].lat);
                        lng = parseFloat(data[0].lon);
                    } else {
                        // Fallback to city center of Germany
                        lat = 51.1657;
                        lng = 10.4515;
                    }
                } catch (error) {
                    console.error('Geocoding error:', error);
                    lat = 51.1657;
                    lng = 10.4515;
                }
            } else {
                // Default center (Germany)
                lat = 51.1657;
                lng = 10.4515;
            }

            // Create map
            const map = window.L.map(mapRef.current).setView([lat, lng], hasAddress ? 15 : 5);
            mapInstance.current = map;

            // Add OpenStreetMap tiles
            window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            }).addTo(map);

            // Add marker if we have a specific location
            if (hasCoordinates || hasAddress) {
                const marker = window.L.marker([lat, lng]).addTo(map);
                
                // Create popup content
                const popupContent = `
                    <div style="min-width: 150px;">
                        <strong>${propertyTitle || 'Immobilie'}</strong>
                        ${address?.street ? `<br>${address.street} ${address.house_number || ''}` : ''}
                        ${address?.zip || address?.city ? `<br>${address.zip || ''} ${address.city || ''}` : ''}
                    </div>
                `;
                marker.bindPopup(popupContent);
            }
        };

        loadLeaflet();

        return () => {
            if (mapInstance.current) {
                mapInstance.current.remove();
                mapInstance.current = null;
            }
        };
    }, [address, hasCoordinates, hasAddress, propertyTitle]);

    if (!hasAddress && !hasCoordinates) {
        return (
            <Card className="border" data-testid="map-no-address">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                        <MapPin size={20} weight="fill" />
                        Standort
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="h-[200px] bg-muted rounded-sm flex items-center justify-center text-muted-foreground">
                        <div className="text-center">
                            <MapPin size={32} className="mx-auto mb-2 opacity-50" />
                            <p>Keine Adresse vorhanden</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="border" data-testid="property-map">
            <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                    <MapPin size={20} weight="fill" />
                    Standort
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div 
                    ref={mapRef} 
                    className="h-[300px] rounded-sm overflow-hidden"
                    style={{ zIndex: 0 }}
                    data-testid="map-container"
                />
                <div className="mt-2 text-sm text-muted-foreground">
                    {[address?.street, address?.house_number].filter(Boolean).join(' ')}
                    {address?.street && (address?.zip || address?.city) && ', '}
                    {[address?.zip, address?.city].filter(Boolean).join(' ')}
                </div>
            </CardContent>
        </Card>
    );
}
